/**
 * A sample equipment register for OEMA.
 *
 * Every date is placed relative to `TODAY` from `./clock`, never written as a
 * literal. That is not tidiness: this register's whole purpose is to show what
 * has expired and what is about to, and a fixture with dates typed into it
 * stops demonstrating that within a fortnight and then quietly shows a clean
 * register for ever. See `src/mock/clock.ts` for what two clocks cost here
 * last time.
 *
 * One item is `awaiting_parts` rather than `unserviceable`, and the two are
 * kept apart on purpose: a machine somebody can schedule work on and one
 * waiting on a supplier are different problems, and a register that shows them
 * as the same state cannot answer "how much of the fleet is down for want of
 * something on order", which is the one fleet figure a maintenance manager
 * can act on this week.
 *
 * The spread is deliberate. Some items are expired, some fall due inside the
 * month, some are clean, and several have a date nobody recorded — because the
 * blank is the state the screen most needs to make visible, and a sample where
 * everything is filled in would never show it.
 *
 * Stakeholder names are roles rather than companies. The register is meant to
 * be renamed by the airport to its actual contract holders; inventing company
 * names that resemble real ground handlers would put fabricated equipment and
 * fabricated defects against organisations that exist.
 */
import type { FleetItem, FleetRegister } from "../domain/fleet";
import { TODAY } from "./clock";

/** A date offset from the sample estate's own clock, as YYYY-MM-DD. */
function day(offset: number): string {
  const d = new Date(TODAY);
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
}

const AIRPORT = { kind: "airport" as const, name: "Tibah Airports Operation" };
const HANDLING = { kind: "stakeholder" as const, name: "Ground handling agent" };
const FUEL = { kind: "stakeholder" as const, name: "Fuel services" };
const CLEANING = { kind: "stakeholder" as const, name: "Terminal cleaning" };
const PAVING = { kind: "stakeholder" as const, name: "Pavement contractor" };
const CATERING = { kind: "stakeholder" as const, name: "Catering services" };

let seq = 0;
const id = () => `eq-${String(++seq).padStart(3, "0")}`;

export function mockFleet(): FleetRegister {
  seq = 0;
  const items: FleetItem[] = [
    /* ── sweepers ──────────────────────────────────────────────────── */
    {
      id: id(),
      tag: "SWP-01",
      department: "Airside Operations",
      name: "Runway sweeper",
      kind: "sweeper",
      owner: AIRPORT,
      make: "Bucher",
      model: "CityCat VR50",
      year: 2021,
      condition: "serviceable",
      location: "RWY-17-35",
      registration: "MED-4421",
      driver: { name: "Faisal Al-Harbi", permit: "ADP-0118", permitExpiry: day(212) },
      licenceExpiry: day(154),
      insuranceExpiry: day(154),
      airsidePermit: { ref: "AVP-0042", expiry: day(96) },
      safetyInspection: { lastAt: day(-72), nextDueAt: day(108), result: "pass" },
      hoursRun: 4180,
      maintenance: [
        {
          at: day(-72),
          what: "500-hour service, brushes replaced",
          kind: "scheduled",
          by: "Plant workshop",
          hours: 4110,
        },
        {
          at: day(-151),
          what: "Hydraulic hose burst on the offside brush",
          kind: "repair",
          by: "Plant workshop",
          workOrder: "WO-000214",
        },
      ],
    },
    {
      id: id(),
      tag: "SWP-02",
      department: "Airside Operations",
      name: "Runway sweeper",
      kind: "sweeper",
      owner: AIRPORT,
      make: "Bucher",
      model: "CityCat VR50",
      year: 2019,
      condition: "awaiting_parts",
      conditionNote: "suction fan bearing failed; parts on order, 3 weeks",
      location: "Plant yard",
      registration: "MED-4422",
      driver: { name: "Faisal Al-Harbi", permit: "ADP-0118", permitExpiry: day(212) },
      licenceExpiry: day(-18),
      insuranceExpiry: day(41),
      airsidePermit: { ref: "AVP-0043", expiry: day(96) },
      safetyInspection: { lastAt: day(-190), nextDueAt: day(-25), result: "pass with observations" },
      hoursRun: 7920,
      maintenance: [
        {
          at: day(-9),
          what: "Fan bearing stripped and assessed",
          kind: "defect",
          by: "Plant workshop",
          workOrder: "WO-000231",
        },
        { at: day(-190), what: "1000-hour service", kind: "scheduled", by: "Plant workshop", hours: 7640 },
      ],
    },
    {
      id: id(),
      tag: "SWP-03",
      department: "Facilities",
      name: "Road sweeper, landside",
      kind: "sweeper",
      owner: CLEANING,
      make: "Johnston",
      model: "C201",
      year: 2022,
      condition: "serviceable",
      location: "Landside forecourt",
      registration: "MED-7731",
      driver: { name: "Yousef Abdullah" },
      licenceExpiry: day(62),
      safetyInspection: { lastAt: day(-40), nextDueAt: day(145), result: "pass" },
      maintenance: [{ at: day(-40), what: "Annual service", kind: "scheduled", by: "Contractor's workshop" }],
    },

    /* ── surface plant ─────────────────────────────────────────────── */
    {
      id: id(),
      tag: "WBL-01",
      department: "Pavements",
      name: "Water blaster, rubber removal",
      kind: "surface",
      owner: AIRPORT,
      make: "Hammelmann",
      model: "HDP 254",
      year: 2020,
      condition: "serviceable",
      location: "Plant yard",
      registration: "MED-4430",
      driver: { name: "Sultan Al-Otaibi", permit: "ADP-0132", permitExpiry: day(11) },
      licenceExpiry: day(203),
      insuranceExpiry: day(203),
      airsidePermit: { ref: "AVP-0051", expiry: day(203) },
      safetyInspection: { lastAt: day(-30), nextDueAt: day(155), result: "pass" },
      hoursRun: 2110,
      maintenance: [
        {
          at: day(-30),
          what: "Pump seals and lance nozzles replaced",
          kind: "scheduled",
          by: "Plant workshop",
          cost: 18400,
        },
        {
          at: day(-118),
          what: "Rubber removal, RWY 17 touchdown zone",
          kind: "inspection",
          by: "Pavements",
          workOrder: "WO-000006",
        },
      ],
    },
    {
      id: id(),
      tag: "FRT-01",
      department: "Pavements",
      name: "Continuous friction measuring device",
      kind: "surface",
      owner: AIRPORT,
      make: "ASFT",
      model: "T-10",
      year: 2023,
      condition: "serviceable",
      location: "Plant yard",
      registration: "MED-4433",
      driver: { name: "Sultan Al-Otaibi", permit: "ADP-0132", permitExpiry: day(11) },
      licenceExpiry: day(287),
      insuranceExpiry: day(287),
      airsidePermit: { ref: "AVP-0052", expiry: day(287) },
      /* calibration is the inspection that matters on this one */
      safetyInspection: { lastAt: day(-88), nextDueAt: day(9), result: "calibrated against reference surface" },
      hoursRun: 640,
      maintenance: [
        { at: day(-88), what: "Annual calibration and water system service", kind: "scheduled", by: "Manufacturer" },
      ],
    },
    {
      id: id(),
      tag: "LNM-01",
      department: "Pavements",
      name: "Line marking unit",
      kind: "surface",
      owner: PAVING,
      make: "Graco",
      model: "LineLazer V",
      year: 2021,
      condition: "degraded",
      conditionNote: "second gun intermittent; single-gun work only",
      location: "Contractor compound",
      driver: { name: "Ibrahim Nasser" },
      licenceExpiry: day(74),
      airsidePermit: { ref: "AVP-0188", expiry: day(21) },
      safetyInspection: { lastAt: day(-60), nextDueAt: day(120) },
      maintenance: [{ at: day(-60), what: "Gun service", kind: "repair", by: "Contractor's workshop" }],
    },

    /* ── tankers ───────────────────────────────────────────────────── */
    {
      id: id(),
      tag: "TNK-01",
      department: "Facilities",
      name: "Water tanker, 12,000 L",
      kind: "tanker",
      owner: AIRPORT,
      make: "Mercedes-Benz",
      model: "Actros 3341",
      year: 2018,
      condition: "serviceable",
      location: "Plant yard",
      registration: "MED-4409",
      driver: { name: "Majed Al-Shammari", permit: "ADP-0090", permitExpiry: day(-6) },
      licenceExpiry: day(33),
      insuranceExpiry: day(33),
      airsidePermit: { ref: "AVP-0031", expiry: day(140) },
      safetyInspection: { lastAt: day(-100), nextDueAt: day(80), result: "pass" },
      hoursRun: 11200,
      maintenance: [
        {
          at: day(-100),
          what: "Brake overhaul and tank inspection",
          kind: "scheduled",
          by: "Plant workshop",
          cost: 42000,
        },
      ],
    },
    {
      id: id(),
      tag: "BWS-01",
      department: "Fuel Services",
      name: "Refueller, 40,000 L",
      kind: "tanker",
      owner: FUEL,
      make: "Scania",
      model: "P410",
      year: 2020,
      condition: "serviceable",
      location: "Fuel farm",
      registration: "MED-9012",
      driver: { name: "Khalid Al-Zahrani", permit: "ADP-0204", permitExpiry: day(310) },
      licenceExpiry: day(198),
      insuranceExpiry: day(198),
      airsidePermit: { ref: "AVP-0210", expiry: day(198) },
      safetyInspection: { lastAt: day(-20), nextDueAt: day(165), result: "pass" },
      maintenance: [
        { at: day(-20), what: "Filter change and bonding continuity test", kind: "scheduled", by: "Fuel services" },
      ],
    },

    /* ── earthmoving ───────────────────────────────────────────────── */
    {
      id: id(),
      tag: "BOB-01",
      department: "Pavements",
      name: "Skid-steer loader",
      kind: "earthmoving",
      owner: AIRPORT,
      make: "Bobcat",
      model: "S650",
      year: 2019,
      condition: "serviceable",
      location: "Plant yard",
      registration: "MED-4415",
      driver: { name: "Nayef Al-Dosari", permit: "ADP-0145", permitExpiry: day(240) },
      licenceExpiry: day(120),
      insuranceExpiry: day(120),
      airsidePermit: { ref: "AVP-0038", expiry: day(120) },
      safetyInspection: { lastAt: day(-55), nextDueAt: day(125), result: "pass" },
      hoursRun: 3300,
      maintenance: [{ at: day(-55), what: "250-hour service", kind: "scheduled", by: "Plant workshop" }],
    },
    {
      id: id(),
      tag: "JCB-01",
      department: "Pavements",
      name: "Backhoe loader",
      kind: "earthmoving",
      owner: AIRPORT,
      make: "JCB",
      model: "3CX",
      year: 2017,
      condition: "degraded",
      conditionNote: "slew ram weeping; usable, watched weekly",
      location: "Plant yard",
      registration: "MED-4402",
      driver: { name: "Nayef Al-Dosari", permit: "ADP-0145", permitExpiry: day(240) },
      licenceExpiry: day(5),
      insuranceExpiry: day(5),
      airsidePermit: { ref: "AVP-0022", expiry: day(75) },
      safetyInspection: { lastAt: day(-170), nextDueAt: day(15), result: "pass with observations" },
      hoursRun: 9100,
      maintenance: [
        {
          at: day(-14),
          what: "Slew ram seal inspection — deferred, parts lead time",
          kind: "defect",
          by: "Plant workshop",
          workOrder: "WO-000226",
        },
        { at: day(-170), what: "Annual service", kind: "scheduled", by: "Plant workshop" },
      ],
    },
    {
      id: id(),
      tag: "GRD-01",
      department: "Pavements",
      name: "Motor grader",
      kind: "earthmoving",
      owner: PAVING,
      make: "Caterpillar",
      model: "120K",
      year: 2016,
      condition: "serviceable",
      location: "Contractor compound",
      driver: { name: "Ahmed Siddiqui" },
      /* the contractor has not supplied these; the blank is the finding */
      safetyInspection: { lastAt: day(-45) },
      maintenance: [{ at: day(-45), what: "Blade and circle service", kind: "scheduled", by: "Contractor's workshop" }],
    },
    {
      id: id(),
      tag: "EXC-01",
      department: "Pavements",
      name: "Wheeled excavator",
      kind: "earthmoving",
      owner: PAVING,
      make: "Volvo",
      model: "EW160E",
      year: 2020,
      condition: "serviceable",
      location: "Apron C works area",
      registration: "MED-8804",
      driver: { name: "Ahmed Siddiqui" },
      licenceExpiry: day(88),
      airsidePermit: { ref: "AVP-0191", expiry: day(21) },
      safetyInspection: { lastAt: day(-33), nextDueAt: day(147), result: "pass" },
      maintenance: [
        { at: day(-33), what: "Track and hydraulic service", kind: "scheduled", by: "Contractor's workshop" },
      ],
    },

    /* ── access platforms ──────────────────────────────────────────── */
    {
      id: id(),
      tag: "SCL-01",
      department: "Building Services",
      usedByDepartment: "Airside Operations",
      name: "Scissor lift, 12 m",
      kind: "access",
      owner: AIRPORT,
      make: "Genie",
      model: "GS-3246",
      year: 2021,
      condition: "serviceable",
      location: "Terminal 1 plant room",
      driver: { name: "Omar Al-Ghamdi", permit: "ADP-0161", permitExpiry: day(180) },
      /* LOLER-style thorough examination is the inspection that matters */
      safetyInspection: { lastAt: day(-140), nextDueAt: day(40), result: "thorough examination passed" },
      hoursRun: 900,
      maintenance: [
        {
          at: day(-140),
          what: "Thorough examination and annual service",
          kind: "inspection",
          by: "Lifting contractor",
        },
      ],
    },
    {
      id: id(),
      tag: "BMT-01",
      department: "Electrical",
      name: "Boom truck, 26 m",
      kind: "access",
      owner: AIRPORT,
      make: "Isuzu",
      model: "FVR with Palfinger boom",
      year: 2018,
      condition: "serviceable",
      location: "Plant yard",
      registration: "MED-4418",
      driver: { name: "Omar Al-Ghamdi", permit: "ADP-0161", permitExpiry: day(180) },
      licenceExpiry: day(-40),
      insuranceExpiry: day(-40),
      airsidePermit: { ref: "AVP-0029", expiry: day(65) },
      safetyInspection: { lastAt: day(-160), nextDueAt: day(20), result: "pass" },
      hoursRun: 5600,
      maintenance: [
        { at: day(-160), what: "Thorough examination, boom and winch", kind: "inspection", by: "Lifting contractor" },
      ],
    },
    {
      id: id(),
      tag: "FLT-01",
      department: "Cargo",
      name: "Forklift, 3 t",
      kind: "access",
      owner: HANDLING,
      make: "Toyota",
      model: "8FG30",
      year: 2022,
      condition: "serviceable",
      location: "Cargo shed",
      driver: { name: "Rashed Al-Qahtani", permit: "ADP-0233", permitExpiry: day(55) },
      safetyInspection: { lastAt: day(-70), nextDueAt: day(110), result: "pass" },
      maintenance: [{ at: day(-70), what: "Service and mast inspection", kind: "scheduled", by: "Handling workshop" }],
    },

    /* ── haulage ───────────────────────────────────────────────────── */
    {
      id: id(),
      tag: "DMP-01",
      department: "Pavements",
      name: "Dump truck, 15 t",
      kind: "haulage",
      owner: PAVING,
      make: "MAN",
      model: "TGS 33.400",
      year: 2019,
      condition: "serviceable",
      location: "Apron C works area",
      registration: "MED-8811",
      driver: { name: "Bilal Haider" },
      licenceExpiry: day(27),
      airsidePermit: { ref: "AVP-0193", expiry: day(21) },
      safetyInspection: { lastAt: day(-52), nextDueAt: day(128), result: "pass" },
      maintenance: [
        { at: day(-52), what: "Tipper ram and body service", kind: "scheduled", by: "Contractor's workshop" },
      ],
    },
    {
      id: id(),
      tag: "FLB-01",
      department: "Fleet & Plant",
      usedByDepartment: "Pavements",
      name: "Flatbed, 10 t",
      kind: "haulage",
      owner: AIRPORT,
      make: "Hino",
      model: "500 Series",
      year: 2020,
      condition: "serviceable",
      location: "Plant yard",
      registration: "MED-4425",
      licenceExpiry: day(133),
      insuranceExpiry: day(133),
      airsidePermit: { ref: "AVP-0046", expiry: day(133) },
      safetyInspection: { lastAt: day(-25), nextDueAt: day(160), result: "pass" },
      maintenance: [{ at: day(-25), what: "Annual service", kind: "scheduled", by: "Plant workshop" }],
    },

    /* ── rescue and fire ───────────────────────────────────────────── */
    {
      id: id(),
      tag: "RFF-01",
      department: "Rescue and Fire Fighting",
      name: "Fire appliance, major foam",
      kind: "rescue",
      owner: AIRPORT,
      make: "Rosenbauer",
      model: "Panther 6x6",
      year: 2019,
      condition: "serviceable",
      location: "Fire station 1",
      registration: "MED-4401",
      driver: { name: "Watch manager, Red watch", permit: "ADP-0001", permitExpiry: day(365) },
      licenceExpiry: day(240),
      insuranceExpiry: day(240),
      airsidePermit: { ref: "AVP-0001", expiry: day(365) },
      safetyInspection: { lastAt: day(-14), nextDueAt: day(76), result: "pass — pump and foam proportioning tested" },
      hoursRun: 1400,
      maintenance: [
        { at: day(-14), what: "Quarterly pump and foam induction test", kind: "inspection", by: "RFFS workshop" },
        { at: day(-104), what: "Quarterly pump and foam induction test", kind: "inspection", by: "RFFS workshop" },
      ],
    },
    {
      id: id(),
      tag: "RFF-02",
      department: "Rescue and Fire Fighting",
      name: "Fire appliance, rapid intervention",
      kind: "rescue",
      owner: AIRPORT,
      make: "Rosenbauer",
      model: "Panther 4x4",
      year: 2019,
      condition: "serviceable",
      location: "Fire station 1",
      registration: "MED-4403",
      driver: { name: "Watch manager, Blue watch", permit: "ADP-0002", permitExpiry: day(365) },
      licenceExpiry: day(240),
      insuranceExpiry: day(240),
      airsidePermit: { ref: "AVP-0002", expiry: day(365) },
      safetyInspection: { lastAt: day(-14), nextDueAt: day(76), result: "pass" },
      hoursRun: 1180,
      maintenance: [
        { at: day(-14), what: "Quarterly pump and foam induction test", kind: "inspection", by: "RFFS workshop" },
      ],
    },
    {
      id: id(),
      tag: "AMB-01",
      department: "Medical",
      name: "Ambulance",
      kind: "rescue",
      owner: AIRPORT,
      make: "Mercedes-Benz",
      model: "Sprinter",
      year: 2021,
      condition: "serviceable",
      location: "Fire station 1",
      registration: "MED-4405",
      driver: { name: "Duty paramedic", permit: "ADP-0003", permitExpiry: day(300) },
      licenceExpiry: day(175),
      insuranceExpiry: day(175),
      airsidePermit: { ref: "AVP-0003", expiry: day(300) },
      safetyInspection: { lastAt: day(-35), nextDueAt: day(145), result: "pass" },
      maintenance: [{ at: day(-35), what: "Service and medical gas check", kind: "scheduled", by: "Plant workshop" }],
    },

    /* ── power and lighting ────────────────────────────────────────── */
    {
      id: id(),
      tag: "GEN-01",
      department: "Electrical",
      name: "Mobile generator, 250 kVA",
      kind: "power",
      owner: AIRPORT,
      make: "Cummins",
      model: "C250D5",
      year: 2020,
      condition: "serviceable",
      location: "Substation B compound",
      safetyInspection: { lastAt: day(-21), nextDueAt: day(69), result: "pass — load bank tested" },
      hoursRun: 310,
      maintenance: [
        { at: day(-21), what: "Quarterly load bank test", kind: "inspection", by: "Electrical" },
        { at: day(-112), what: "Fuel polishing and filter change", kind: "scheduled", by: "Electrical" },
      ],
    },
    {
      id: id(),
      tag: "GEN-02",
      department: "Electrical",
      name: "Mobile generator, 100 kVA",
      kind: "power",
      owner: AIRPORT,
      make: "FG Wilson",
      model: "P110",
      year: 2016,
      condition: "unserviceable",
      conditionNote: "failed load bank test — alternator winding; quoted, awaiting approval",
      location: "Plant yard",
      safetyInspection: { lastAt: day(-21), nextDueAt: day(-3), result: "fail" },
      hoursRun: 2900,
      maintenance: [
        {
          at: day(-21),
          what: "Quarterly load bank test — failed",
          kind: "defect",
          by: "Electrical",
          workOrder: "WO-000229",
        },
      ],
    },
    {
      id: id(),
      tag: "LTT-01",
      department: "Pavements",
      usedByDepartment: "Airside Operations",
      name: "Lighting tower",
      kind: "power",
      owner: PAVING,
      make: "Atlas Copco",
      model: "HiLight V5+",
      year: 2022,
      condition: "serviceable",
      location: "Apron C works area",
      driver: { name: "Ahmed Siddiqui" },
      airsidePermit: { ref: "AVP-0195", expiry: day(21) },
      safetyInspection: { lastAt: day(-48), nextDueAt: day(132) },
      maintenance: [{ at: day(-48), what: "Service", kind: "scheduled", by: "Contractor's workshop" }],
    },

    /* ── ground support ────────────────────────────────────────────── */
    {
      id: id(),
      tag: "TUG-01",
      department: "Ramp",
      name: "Pushback tug, wide-body",
      kind: "gse",
      owner: HANDLING,
      make: "Goldhofer",
      model: "AST-2X",
      year: 2019,
      condition: "serviceable",
      location: "Stand 12",
      driver: { name: "Rashed Al-Qahtani", permit: "ADP-0233", permitExpiry: day(55) },
      registration: "MED-6601",
      licenceExpiry: day(210),
      airsidePermit: { ref: "AVP-0240", expiry: day(210) },
      safetyInspection: { lastAt: day(-29), nextDueAt: day(151), result: "pass" },
      maintenance: [
        { at: day(-29), what: "Service and towbarless clamp inspection", kind: "scheduled", by: "Handling workshop" },
      ],
    },
    {
      id: id(),
      tag: "BLT-01",
      department: "Ramp",
      name: "Belt loader",
      kind: "gse",
      owner: HANDLING,
      make: "TLD",
      model: "NBL-2",
      year: 2018,
      condition: "degraded",
      conditionNote: "belt tracking off; loading at reduced rate",
      location: "Stand 8",
      driver: { name: "Not assigned this shift" },
      airsidePermit: { ref: "AVP-0244", expiry: day(48) },
      safetyInspection: { lastAt: day(-95), nextDueAt: day(85) },
      maintenance: [
        { at: day(-12), what: "Belt tracking adjusted, to be re-checked", kind: "repair", by: "Handling workshop" },
      ],
    },
    {
      id: id(),
      tag: "STP-01",
      department: "Ramp",
      name: "Passenger steps",
      kind: "gse",
      owner: HANDLING,
      make: "Mallaghan",
      model: "PS-360",
      year: 2021,
      condition: "serviceable",
      location: "Stand 5",
      airsidePermit: { ref: "AVP-0247", expiry: day(48) },
      safetyInspection: { lastAt: day(-64), nextDueAt: day(116), result: "pass" },
      maintenance: [
        { at: day(-64), what: "Service and handrail inspection", kind: "scheduled", by: "Handling workshop" },
      ],
    },
    {
      id: id(),
      tag: "CAT-01",
      department: "Catering",
      name: "Catering hi-loader",
      kind: "gse",
      owner: CATERING,
      make: "Mallaghan",
      model: "CT6000",
      year: 2020,
      condition: "serviceable",
      location: "Catering compound",
      driver: { name: "Tariq Mansour" },
      airsidePermit: { ref: "AVP-0260", expiry: day(-11) },
      safetyInspection: { lastAt: day(-58), nextDueAt: day(122), result: "pass" },
      maintenance: [{ at: day(-58), what: "Scissor and body service", kind: "scheduled", by: "Catering workshop" }],
    },

    /* ── vehicles ──────────────────────────────────────────────────── */
    {
      id: id(),
      tag: "OPS-01",
      department: "Airside Operations",
      name: "Follow-me vehicle",
      kind: "vehicle",
      owner: AIRPORT,
      make: "Toyota",
      model: "Land Cruiser",
      year: 2022,
      condition: "serviceable",
      location: "Airside operations",
      registration: "MED-4440",
      driver: { name: "Duty airside officer", permit: "ADP-0010", permitExpiry: day(150) },
      licenceExpiry: day(260),
      insuranceExpiry: day(260),
      airsidePermit: { ref: "AVP-0010", expiry: day(260) },
      safetyInspection: { lastAt: day(-18), nextDueAt: day(162), result: "pass" },
      maintenance: [{ at: day(-18), what: "Service and beacon check", kind: "scheduled", by: "Plant workshop" }],
    },
    {
      id: id(),
      tag: "OPS-02",
      department: "Airside Operations",
      name: "Airside operations pickup",
      kind: "vehicle",
      owner: AIRPORT,
      make: "Toyota",
      model: "Hilux",
      year: 2021,
      condition: "serviceable",
      location: "Airside operations",
      registration: "MED-4441",
      driver: { name: "Mona Al-Rashid", permit: "ADP-0022", permitExpiry: day(23) },
      licenceExpiry: day(19),
      insuranceExpiry: day(19),
      airsidePermit: { ref: "AVP-0011", expiry: day(19) },
      safetyInspection: { lastAt: day(-80), nextDueAt: day(100), result: "pass" },
      maintenance: [{ at: day(-80), what: "Service", kind: "scheduled", by: "Plant workshop" }],
    },
    {
      id: id(),
      tag: "ENG-01",
      department: "Engineering",
      name: "Engineering van",
      kind: "vehicle",
      owner: AIRPORT,
      make: "Ford",
      model: "Transit",
      year: 2019,
      condition: "serviceable",
      location: "Engineering workshop",
      registration: "MED-4443",
      driver: { name: "Hassan Ali", permit: "ADP-0077", permitExpiry: day(95) },
      licenceExpiry: day(72),
      insuranceExpiry: day(72),
      airsidePermit: { ref: "AVP-0018", expiry: day(72) },
      safetyInspection: { lastAt: day(-110), nextDueAt: day(70), result: "pass" },
      maintenance: [{ at: day(-110), what: "Service and brake replacement", kind: "scheduled", by: "Plant workshop" }],
    },
    {
      id: id(),
      tag: "BUS-01",
      department: "Ramp",
      name: "Crew bus, 30 seat",
      kind: "vehicle",
      owner: HANDLING,
      make: "Cobus",
      model: "2700S",
      year: 2017,
      condition: "serviceable",
      location: "Stand 3",
      registration: "MED-6620",
      driver: { name: "Adel Farouk", permit: "ADP-0251", permitExpiry: day(140) },
      licenceExpiry: day(101),
      airsidePermit: { ref: "AVP-0251", expiry: day(101) },
      safetyInspection: { lastAt: day(-77), nextDueAt: day(103), result: "pass" },
      maintenance: [
        { at: day(-77), what: "Service and door mechanism overhaul", kind: "scheduled", by: "Handling workshop" },
      ],
    },
  ];

  return {
    items,
    enteredBy: "the demonstration",
    enteredAt: new Date(TODAY).toISOString(),
  };
}
